import React, { useState } from 'react';
import { Alert, Text } from '@groupelacentrale/contact-storybook';
import { useFormik, FormikErrors } from 'formik';
import { useDeposit } from 'providers';
import { ContactInfoProResponse, ContactInformationPro, User } from 'types';
import { ContactInformationProFormView } from './ContactInformationProFormView';
import { validationSchema } from './validationSchema';
import { ROUTES } from 'routes';
import { useHistory } from 'react-router-dom';
import { useReturn } from 'hooks';
import { ClassifiedManagementService } from 'api/Classifieds/ClassifiedManagementService';
import { Loader } from 'components';
import { undefinedIfEmpty } from 'utils';
import { useMountEffect } from 'hooks/use-mount-effect';
import { Tracking } from 'api';

interface Props {
  user: User;
}

export const ContactInformationProForm: React.FC<Props> = ({ user }) => {
  const { contacts, zipCode, setContacts } = useDeposit();
  const history = useHistory();
  const [contactProInfo, setContactProInfo] = useState<ContactInfoProResponse | undefined>(undefined);

  useMountEffect(() => {
    ClassifiedManagementService.getProContactInfo(user.username).then(r => {
      if (contacts?.email && !r.emails.includes(contacts.email)) {
        setContactProInfo({ ...r, emails: [contacts.email, ...r.emails] });
      } else {
        setContactProInfo(r);
      }
    });
  });

  // if we have contacts.phone1 infos from classified, so a first save have been made
  // so we use classified contact otherwise we use customer pro account infos
  const isContactInfosUpdated = !!contacts?.phone1?.value;

  const formikBag = useFormik<ContactInformationPro>({
    enableReinitialize: true,
    initialValues: {
      phone: contacts?.phone1?.value || contactProInfo?.phone1 || '',
      contact: isContactInfosUpdated ? contacts?.phone1?.note || '' : contactProInfo?.contact1 || '',
      phone2: isContactInfosUpdated ? contacts?.phone2?.value || '' : contactProInfo?.phone2 || '',
      contact2: contacts?.phone2?.note,
      email: contacts?.email || (contactProInfo?.emails.length === 1 ? contactProInfo?.emails[0] : ''),
      zipCode: zipCode || contactProInfo?.zipCode || '',
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setErrors }) => {
      const errors: FormikErrors<ContactInformationPro> = {};
      if (values.email && !contactProInfo?.emails.includes(values.email)) {
        errors.email = 'Veuillez choisir une adresse mail parmi la liste.';
      }

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setSubmitting(false);
        Tracking.sendCurrentPageError();
        return;
      }

      setContacts(
        {
          email: values.email!,
          phone1: { value: values.phone, note: undefinedIfEmpty(values.contact) },
          phone2:
            values.phone2 && values.phone2.trim().length > 0
              ? { value: values.phone2, note: undefinedIfEmpty(values.contact2) }
              : null,
        },
        undefined,
        undefined,
        values.zipCode && values.zipCode.trim().length > 0 ? values.zipCode : null,
      ).then(() => {
        history.push(ROUTES.vehiclePrice);
      });
    },
  });

  const onReturn = useReturn(ROUTES.photos);

  if (!contactProInfo) {
    return <Loader />;
  } else if (contactProInfo.emails.length === 0) {
    return (
      <Alert type="error">
        <Text size="medium" weight="bold">
          Votre compte ne comporte pas d&apos;adresse email. Veuillez contacter le service professionnel.
        </Text>
      </Alert>
    );
  } else {
    return <ContactInformationProFormView emails={contactProInfo.emails} handleReturn={onReturn} {...formikBag} />;
  }
};
